/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 */
import _ from 'lodash';
import React from 'react';
import { decorate, computed, action, runInAction, observable } from 'mobx';
import { observer, inject } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import { Icon } from 'semantic-ui-react';

import { displayError } from '@amzn/base-ui/dist/helpers/notification';

class ScEnvironmentDcvConnectionRow extends React.Component {
  constructor(props) {
    super(props);
    runInAction(() => {
      this.generatingBrowserToken = false;
      this.generatingClientToken = false;
      this.dcvWarningDismissed = false;
    });
  }

  get environment() { return this.props.scEnvironment; }
  get envsStore() { return this.props.scEnvironmentsStore; }

  getConnectionStore() {
    return this.envsStore.getScEnvConnectionStore(this.environment.id);
  }

  get connections() {
    return this.environment.getConnections(item => item.scheme === 'dcv');
  }

  get connection() {
    return _.find(this.connections, ['id', this.connectionId]) || {};
  }

  get connectionId() { return this.props.connectionId; }

  handleConnectBrowser = id => this.handleConnect(id, false);
  handleConnectClient = id => this.handleConnect(id, true);

  handleConnect = (id, useClientProtocol) => action(async () => {
    const store = this.getConnectionStore();
    const connections = this.environment.connections;
    const connectInfo = _.find(connections, ['id', id]) || {};
    let url = connectInfo.url;
    const urlLoaded = !!url;
    let newWindow;

    if (urlLoaded && useClientProtocol) {
      url = url.replace('https://', 'dcv://');
    }

    runInAction(() => {
      this.processingId = id;
      if (useClientProtocol) {
        this.generatingClientToken = true;
      } else {
        this.generatingBrowserToken = true;
      }
    });

    try {
      newWindow = window.open(`${window.location.origin}/${useClientProtocol ? 'dcv' : 'transit'}.html${url ? `?redirect=${url}` : ''}`, '_blank');
      if (!newWindow) {
        throw new Error('Popup blocked. Please allow popups for this site.');
      }

      if (!url) {
        const urlObj = await store.createConnectionUrl(id);
        url = urlObj.url;
      }

      if (!urlLoaded && url && newWindow) {
        if (useClientProtocol) {
          url = url.replace('https://', 'dcv://');
        }
        newWindow.postMessage({ redirect: url }, window.location.origin);
      }
    } catch (error) {
      if (newWindow) newWindow.close();
      displayError(error);
    } finally {
      runInAction(() => {
        this.processingId = '';
        this.generatingBrowserToken = false;
        this.generatingClientToken = false;
      });
    }
  });

  dismissDcvWarning = () => { this.dcvWarningDismissed = true; };

  render() {
    const connections = this.connections;
    const primaryRegion = process.env.REACT_APP_AWS_REGION;
    const targetRegion = this.environment.targetRegion;
    const isRemoteRegion = targetRegion && targetRegion !== primaryRegion;

    return (
      <>
        {isRemoteRegion && !this.dcvWarningDismissed && (
          <div className="workspace-alert workspace-alert-warning" style={{ marginBottom: '12px' }}>
            <Icon name="warning sign" style={{ margin: 0, lineHeight: 1, height: '1em' }} />
            <span style={{ flex: 1 }}>
              <strong>Performance notice:</strong> This workspace is in a non-Australian region. DCV streaming may experience higher latency.
              For the best experience, consider using the DCV Client application instead of the browser.
            </span>
            <button
              onClick={this.dismissDcvWarning}
              aria-label="Dismiss warning"
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: '#92400e', lineHeight: 1, flexShrink: 0 }}
            >
              <Icon name="close" style={{ margin: 0, lineHeight: 1, height: '1em' }} />
            </button>
          </div>
        )}
        {_.map(connections, item => (
          <div className="connection-row" key={item.id}>
            <div className="connection-row-name">{item.name || 'Linux Desktop'}</div>
            <div className="connection-row-actions">
              <button
                className="connection-btn connection-btn-primary"
                disabled={this.generatingBrowserToken}
                onClick={this.handleConnectBrowser(item.id)}
                data-testid="connect-using-dcv-button"
              >
                {this.generatingBrowserToken ? <Icon name="spinner" loading style={{ margin: 0 }} /> : <Icon name="external" style={{ margin: 0 }} />}
                Open in Browser
              </button>
              <button
                className="connection-btn connection-btn-secondary"
                disabled={this.generatingClientToken}
                onClick={this.handleConnectClient(item.id)}
                data-testid="connect-using-dcv-button"
              >
                {this.generatingClientToken ? <Icon name="spinner" loading style={{ margin: 0 }} /> : <Icon name="window maximize" style={{ margin: 0 }} />}
                Open in DCV Client
              </button>
            </div>
          </div>
        ))}
      </>
    );
  }
}

decorate(ScEnvironmentDcvConnectionRow, {
  envsStore: computed,
  environment: computed,
  connections: computed,
  connection: computed,
  connectionId: computed,
  handleConnect: action,
  generatingBrowserToken: observable,
  generatingClientToken: observable,
  dcvWarningDismissed: observable,
  dismissDcvWarning: action,
});

export default inject('scEnvironmentsStore')(withRouter(observer(ScEnvironmentDcvConnectionRow)));
